import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs';
import { filter, map, switchMap, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { AccountInfoService } from '~app/core/modules/identity/services/account-info.service';
import { FF_ACCOUNT_INFO_V2 } from '~app/modules/authorization/constants/authorization.constant';
import {
  FF_CHECKOUT_ADDRESS_VALIDATION_V2,
  FF_CHECKOUT_AUTH_PAYMENT_V2,
  FF_CHECKOUT_GET_PAYMENT_METHODS_V2,
  FF_CHECKOUT_MAKE_PAYMENT_V2,
  FF_CHECKOUT_REACT_CONSENT,
  FF_TEMP_CO_DATE_OF_BIRTH,
  FF_TEMP_CO_STEPPER_GA_TRACK
} from '~app/modules/checkout/constants/checkout-feature-flags';
import {
  Address,
  AddressValidationParams,
  AddressValidationResponse,
  ChallengeDetailsRequestModel,
  DiscountCode,
  Order,
  OrderPayment,
  PaymentMethodsConfig,
  PaymentMethodsConfigResponse,
  PaymentRequest,
  ServicePaymentMethodTypes,
  UserEmailConsentResponse,
} from '~app/modules/checkout/models';
import { UserPurchaseHistoryResponseModel } from '~app/modules/checkout/models/user-purchase-history-response.model';
import { CheckoutCommonUtil } from '~app/modules/checkout/utils/checkout-common-util';
import { ApiConfig } from '~shared/constants/api.config';
import { AccountType } from '~shared/models';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { ServerLoggerService } from '~shared/modules/server-logger/services/server-logger.service';
import { PaymentErrorMessages, PaymentMethodOptions, PaymentNewTag } from '../constants';
import { catchError } from 'rxjs/operators';
import { OfferHelperService } from '~shared/modules/offer-line/services/offer-helper.service';
import { Store, select } from '@ngrx/store';
import { CartOfferLine, CartState, selectOffers } from '~shared/modules/shopping-cart-store';
import { InvalidOfferOverlayComponent } from '../components/invalid-offer-overlay/invalid-offer-overlay.component';
import { MmDialogService } from '@metromarkets/components';
import { INVALID_OFFER_MESSAGE } from '~shared/modules/offer-line/constants/offer.constant';
import { AddressValidationDialogComponent } from '../components/address-validation-dialog/address-validation-dialog.component';
import { Overlay } from '@angular/cdk/overlay';
import {
  AddressValidationErrorTypes,
  PREODER_VALIDATION_INVALID_SHIPPING,
  VALIDATION_FIELDS_MAP,
} from '../constants/address-validation.constants';
import { TranslateService } from '@ngx-translate/core';
import { CountryCode, FeatureToggleEnum } from '~shared/constants';
import { environment } from '~env/environment';
import { PaymentUtils } from '~app/modules/checkout/utils/payment-util';

@Injectable({
  providedIn: 'root',
})
export class CheckoutService {
  private showDateOfBirth = new BehaviorSubject<boolean>(false);
  showDateOfBirth$ = this.showDateOfBirth.asObservable();
  private paymentErrorMessage: { message?: PaymentErrorMessages; params?: Record<string, string> } | null = null;

  enableStepperTracking = false;

  constructor(
    private httpClient: HttpClient,
    private activeFeaturesConfigCatService: ActiveFeaturesConfigcatService,
    private serverLogger: ServerLoggerService,
    private offerHelperService: OfferHelperService,
    private accountInfoService: AccountInfoService,
    private store$: Store<CartState>,
    private mmDialogService: MmDialogService,
    private overlay: Overlay,
    private translateService: TranslateService
  ) {
    this.initFeatureFlags();
  }

  initFeatureFlags() {
    this.checkDateOfBirthField();
    this.isStepperGATrackingEnabled();
  }

  checkDateOfBirthField() {
    this.activeFeaturesConfigCatService.isFeatureEnabled(FF_TEMP_CO_DATE_OF_BIRTH).subscribe((showDateOfBirth) => {
      this.showDateOfBirth.next(showDateOfBirth);
    });
  }

  isStepperGATrackingEnabled() {
    this.activeFeaturesConfigCatService
      .isFeatureEnabled(FF_TEMP_CO_STEPPER_GA_TRACK)
      .subscribe((enableStepperTracking) => {
        this.enableStepperTracking = enableStepperTracking;
      });
  }

  get checkStepperGATrack() {
    return this.enableStepperTracking;
  }

  createOrder(order: { billingAddress: Address; shippingAddress: Address }): Observable<Order | any> {
    return this.httpClient.post(`${ApiConfig.SVC_CHECKOUT_ORDERS()}`, order);
  }

  updateOrder(freightForwardPhone: string, orderId: string): Observable<Order | any> {
    const payload = { status: 1, freightForwardPhone };
    const url = `${ApiConfig.SVC_CHECKOUT_ORDERS()}/${orderId}`;
    return this.httpClient.patch(url, payload);
  }

  initPayment(address: {
    shippingAddress: Address;
    billingAddress: Address;
    freightforwardingPhone?: string;
    organizationVatNumber?: string;
  }): Observable<PaymentMethodsConfig> {
    const discountCode = CheckoutCommonUtil.getTmpDiscountCodeAppliedFromSessionStorage();
    const orderNumber = CheckoutCommonUtil.getTmpOrderNumberFromLocalStorage();
    const logOrderNumber = CheckoutCommonUtil.getOrderNumberFromLocalForLogging();
    const sessionId = CheckoutCommonUtil.getSessionIdFromSessionStorage();
    const params = {
      billingAddress: address.billingAddress,
      shippingAddress: address.shippingAddress,
      orderNumber,
      discountCode,
      freightForwardPhone: address.freightforwardingPhone ?? null,
      organizationVatNumber: address.organizationVatNumber ?? CheckoutCommonUtil.getTmpVatIdFromSessionStorage(),
    };

    this.serverLogger.info(`Checkout - FE - Get Widget Data Request`, {
      ...params,
      logOrderNumber,
      sessionId,
    });

    return this.activeFeaturesConfigCatService
      .isFeatureEnabled(FF_CHECKOUT_GET_PAYMENT_METHODS_V2)
      .pipe(
        switchMap((isEnabled) => {
          let url: string;

          if (isEnabled) {
            const baseUrl = ApiConfig.SVC_BUYER_GATEWAY_URL_API_ENDPOINT();
            url = `${baseUrl}/api/account/proxy/checkout_v2/payment/get-payment-methods`;
          } else {
            url = `${ApiConfig.SVC_API_URL()}/checkout/get-widget-data`;
          }

          return this.httpClient.post<PaymentMethodsConfigResponse>(url, params);
        })
      )
      .pipe(catchError((error) => this.offerHelperService.handleInvalidOffersError(error)));
  }

  makePayment(payment: PaymentRequest, isAssistedSales?: boolean): Observable<OrderPayment> {
    let url = `${ApiConfig.SVC_API_URL_V2()}/checkout/make-payment`;
    const orderNumber = CheckoutCommonUtil.getTmpOrderNumberFromLocalStorage();
    const purchaseOrderNumber = CheckoutCommonUtil.getPurchaseOrderNumberFromLocalStorage();
    const logOrderNumber = CheckoutCommonUtil.getOrderNumberFromLocalForLogging();
    const sessionId = CheckoutCommonUtil.getSessionIdFromSessionStorage();

    let params = {
      ...payment,
      orderNumber,
      browserInfo: PaymentUtils.getBrowserInfo(),
      purchaseOrderNumber: purchaseOrderNumber ?? null,
    };
    this.serverLogger.info(`Checkout - FE - Make Payment Request`, {
      ...params,
      logOrderNumber,
      sessionId,
    });
    let headers = new HttpHeaders();
    if (isAssistedSales && !!AccountInfoService.impersonationSessionId) {
      headers = headers.set('x-impersonation-session-id', AccountInfoService.impersonationSessionId);
    }

    return this.activeFeaturesConfigCatService
      .isFeatureEnabled(FF_CHECKOUT_MAKE_PAYMENT_V2)
      .pipe(
        switchMap((isEnabled) => {
          if (isEnabled) {
            const baseUrl = ApiConfig.SVC_BUYER_GATEWAY_URL_API_ENDPOINT();
            url = `${baseUrl}/api/account/proxy/checkout_v2/checkout/place-order`;
          }

          return this.httpClient.post<OrderPayment>(url, params, { headers });
        })
      )
      .pipe(catchError((error) => this.offerHelperService.handleInvalidOffersError(error)));
  }

  paymentDetails(payment: ChallengeDetailsRequestModel): Observable<OrderPayment> {
    let url = `${ApiConfig.SVC_API_URL()}/checkout/payment-details`;
    const logOrderNumber = CheckoutCommonUtil.getOrderNumberFromLocalForLogging();
    const sessionId = CheckoutCommonUtil.getSessionIdFromSessionStorage();

    this.serverLogger.info(`Checkout - FE - Payment Details Request`, {
      ...payment,
      logOrderNumber,
      sessionId,
    });

    return this.activeFeaturesConfigCatService.isFeatureEnabled(FF_CHECKOUT_AUTH_PAYMENT_V2).pipe(
      switchMap((isEnabled) => {
        if (isEnabled) {
          const baseUrl = ApiConfig.SVC_BUYER_GATEWAY_URL_API_ENDPOINT();
          url = `${baseUrl}/api/account/proxy/checkout_v2/checkout/authenticate-payment`;
        }

        return this.httpClient.post<OrderPayment>(url, payment);
      })
    );
  }

  getUserEmailConsent(): Observable<UserEmailConsentResponse> {
    return this.activeFeaturesConfigCatService.isFeatureEnabled(FF_CHECKOUT_REACT_CONSENT).pipe(
      switchMap((isEnabled) => {
        const url = isEnabled
          ? ApiConfig.BUYER_REACT_CART_CONSENT_URL()
          : `${ApiConfig.SVC_API_URL()}/customer/email/consent`;

        return this.httpClient.get<UserEmailConsentResponse>(url);
      })
    );
  }

  postUserEmailConsent(): Observable<boolean> {
    return this.activeFeaturesConfigCatService.isFeatureEnabled(FF_CHECKOUT_REACT_CONSENT).pipe(
      switchMap((isEnabled) => {
        const url = isEnabled
          ? ApiConfig.BUYER_REACT_CART_CONSENT_URL()
          : `${ApiConfig.SVC_API_URL()}/customer/email/consent`;

        return this.httpClient.post<boolean>(url, {});
      })
    );
  }

  postDiscountCode(code: string): Observable<DiscountCode> {
    const url = `${ApiConfig.SVC_API_URL()}/discount/check/code?code=${code}`;
    return this.httpClient.get<DiscountCode>(url);
  }

  getUserPurchaseHistoryOptOutLegitimateComms(
    market: string,
    buyerId: string
  ): Observable<UserPurchaseHistoryResponseModel> {
    const url = `${ApiConfig.SVC_CRM_EVENTS_URL()}/has-purchases`;
    return this.httpClient.get<UserPurchaseHistoryResponseModel>(url, {
      params: {
        market,
        hardId: buyerId,
      },
    });
  }

  postUserOptOutLegitimateComms(market: string, buyerId: string): Observable<boolean> {
    const url = `${ApiConfig.SVC_CRM_EVENTS_URL()}/consent/reject`;
    return this.httpClient.post<boolean>(url, {
      hardId: buyerId,
      market,
      source: 'checkout',
    });
  }

  getSupportedCreditCards(response: {
    availablePaymentMethods: { creditCard: { options: { paymentMethods: any } } };
  }): string[] {
    return (
      response.availablePaymentMethods?.creditCard?.options?.paymentMethods?.groups?.find(
        (v) => v.name === 'Credit Card'
      )?.types || []
    );
  }

  setPaymentErrorMessage(error: { message?: PaymentErrorMessages; params?: Record<string, string> } | null) {
    this.paymentErrorMessage = error;
  }

  get paymentError(): { message?: PaymentErrorMessages; params?: Record<string, string> } | null {
    return this.paymentErrorMessage;
  }

  isNewPaymentMethod(paymentMethod: PaymentMethodOptions): boolean {
    if (!PaymentNewTag[paymentMethod]) {
      return false;
    }
    return PaymentNewTag[paymentMethod] >= new Date();
  }

  getAssistedSalesFlag(until$?: Subject<void>): Observable<boolean> {
    return combineLatest([
      this.activeFeaturesConfigCatService.isFeatureEnabledReadyForCurrentCountry$(FF_ACCOUNT_INFO_V2),
      this.accountInfoService.accountType$,
    ]).pipe(
      until$ ? takeUntil(until$) : tap((v) => v),
      map(([isFFEnabled, accountType]) => {
        return isFFEnabled
          ? accountType === AccountType.EMPLOYEE || accountType === AccountType.EMPLOYEE_AS_CUSTOMER
          : false;
      })
    );
  }

  validateProductsAvailability() {
    this.store$
      .pipe(
        select(selectOffers),
        take(1),
        map(this.getInactiveOffers),
        filter((offers) => !!offers.length)
      )
      .subscribe((offers) => this.showInvalidOfferOverlay(offers));
  }

  validateShippingAvailability([shipping, billing]: [Address, Address]) {
    if (!shipping || !billing) {
      return of(true);
    }

    const isFeatureEnabled$ = this.activeFeaturesConfigCatService.isFeatureEnabled(FF_CHECKOUT_ADDRESS_VALIDATION_V2);
    let isFeatureEnabled = false;
    return isFeatureEnabled$.pipe(
      switchMap((isEnabled) => {
        isFeatureEnabled = isEnabled;
        const url = isEnabled
          ? `${ApiConfig.SVC_BUYER_GATEWAY_URL_API_ENDPOINT()}/api/account/proxy/checkout_v2/checkout/addresses/validate`
          : `${ApiConfig.SVC_API_URL()}/checkout/validate-addresses`;

        return this.httpClient.post<AddressValidationResponse<AddressValidationParams>>(url, {
          shippingAddress: shipping,
          billingAddress: billing,
        });
      }),
      withLatestFrom(isFeatureEnabled$),
      map(([validation, isEnabled]) => {
        const errorType = isEnabled
          ? AddressValidationErrorTypes.INVALID_SHIPPING_ADDRESS.toLowerCase()
          : AddressValidationErrorTypes.INVALID_SHIPPING_ADDRESS;

        if (validation.type !== errorType) {
          return true;
        }
        this.openAddressValidationDialog(validation.params);
        return false;
      }),
      catchError((e) => {
        if (!isFeatureEnabled) return of(true);
        try {
          const {
            sourceError: {
              error: { errors },
            },
          } = e;
          const validationErrors = errors.filter((err) => err.type === PREODER_VALIDATION_INVALID_SHIPPING);
          if (validationErrors.length > 0) {
            const errorParams = validationErrors[0].fields.reduce(
              (a, c) => ({ ...a, [VALIDATION_FIELDS_MAP[c.name] ?? c.name]: c.value }),
              {}
            );
            this.openAddressValidationDialog(errorParams);
          }
        } catch (e) {}
        return of(true);
      })
    );
  }

  private openAddressValidationDialog(errorParams: Record<string, any>) {
    this.mmDialogService.open(AddressValidationDialogComponent, {
      backdropDisableClose: true,
      panelClass: 'location-validation-dialog',
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      data: {
        ...errorParams,
      },
    });
  }

  private getInactiveOffers(offers: CartOfferLine[]) {
    return offers.filter(
      (offer) => !!offer.invalidParams.find((invalidParam) => invalidParam.type === INVALID_OFFER_MESSAGE)
    );
  }

  private showInvalidOfferOverlay(offers: CartOfferLine[]) {
    this.mmDialogService.open(InvalidOfferOverlayComponent, {
      backdropDisableClose: true,
      panelClass: 'invalid-offer-dialog',
      data: {
        invalidOffers: offers,
      },
    });
  }

  public isTranslationValid(key: string, postfixCodes?: CountryCode[]): boolean {
    const finalKey = `${key}${postfixCodes ? CheckoutCommonUtil.getTranslationMarketPostKey(postfixCodes) : ''}`;
    return this.translateService.instant(finalKey) !== finalKey;
  }

  public isReviewPromoDisabled$(
    paymentMethod: (typeof ServicePaymentMethodTypes)[keyof typeof ServicePaymentMethodTypes]
  ): Observable<boolean> {
    return this.activeFeaturesConfigCatService.getFeatureValue(FeatureToggleEnum.FF_CHECKOUT_REVIEW_PROMO_DISABLE).pipe(
      take(1),
      switchMap((r) => of(!!r?.includes?.(paymentMethod)))
    );
  }

  public getVatIdStatus(orderNumber: string, vatId: string): Observable<any> {
    const url = `${ApiConfig.SVC_BUYER_API_URL()}/business-account/check/${orderNumber}?vatId=${vatId}`;
    return this.httpClient.get(url);
  }

  public saveValidVatId(vatId): Observable<any> {
    return this.accountInfoService.getActiveContext().pipe(
      switchMap((accountId) => {
        const url = `${environment.svcBuyerAccountBaseUrl()}/api/account/v1/business-account/${accountId}/vat-id`;
        return this.httpClient.post(url, {
          vatId: vatId,
        });
      })
    );
  }

  public getSavePaymentMethodFlagFF(flag: string, until: Subject<any>): Observable<boolean> {
    return combineLatest([
      this.activeFeaturesConfigCatService.isFeatureEnabled(flag),
      this.getAssistedSalesFlag(until),
    ]).pipe(
      takeUntil(until),
      map(([flag, assistedSales]) => flag && !assistedSales)
    );
  }
}
